
import { Component, MixedVue } from "@/vue";
import { CSSProperties } from "@vue/runtime-dom";
import { VNode } from "vue";
import { Prop, Ref, Watch } from "vue-property-decorator";
import MenuItem from "./MenuItem.vue";

export type MenuPlacement = "top-left" | "top-right" | "bottom-right" | "bottom-left";

export type MenuAnchors = "start-start" | "start-end" | "end-start" | "end-end";

@Component({
  name: "Menu",
})
export default class Menu extends MixedVue {
  $props!: {
    menuStyle?: CSSProperties;
    placement?: MenuPlacement;
    anchors?: MenuAnchors;
    closeOnClickOutside?: boolean;
    activatorPadding?: CSSStyleDeclaration["padding"];
    beforeClose?: () => boolean;
    activatorActiveClass?: string;
    disabled?: boolean;
  };
  $scopedSlots: {
    default: (bindings: { closeMenu: () => boolean }) => VNode[];
    activator: (bindings: {}) => VNode[];
  };

  $emit!: ((e: "open", open: boolean) => any) & ((e: "close") => any);

  @Prop({ type: Object, required: false, default: () => {} })
  readonly menuStyle?: CSSProperties;

  @Prop({ type: String, required: false, default: "bottom-right" })
  readonly placement?: MenuPlacement;

  @Prop({ type: String, required: false, default: "end-start" })
  readonly anchors?: MenuAnchors;

  @Prop({ type: Boolean, required: false, default: true })
  readonly closeOnClickOutside?: boolean;

  /** top right buttom left */
  @Prop({ type: String, required: false })
  readonly activatorPadding?: CSSStyleDeclaration["padding"];

  @Prop({ type: Function, required: false, default: () => () => true })
  readonly beforeClose?: () => boolean;

  @Prop({ type: String, required: false, default: "active" })
  readonly activatorActiveClass?: string;

  @Prop({ type: Boolean, required: false, default: false })
  readonly disabled?: boolean;

  @Ref()
  activator!: HTMLDivElement;

  showMenu: boolean = false;
  isNestedMenu: boolean = null;
  itemList: HTMLButtonElement[] = [];
  selectedItemIndex: number = null;
  selectedNestedMenuItem: Menu = null;
  lastOpenendNestedMenu: Menu = null;
  lastFocusedIndex: number = null;

  declare $parent: MenuItem;

  // Propagate these values to the parent Menu component
  @Watch("selectedNestedMenuItem")
  selectedNestedMenuItemChange(to: Menu) {
    if (this.isNestedMenu) {
      this.$parent.$parent.selectedNestedMenuItem = to;
    }
  }

  @Watch("lastOpenendNestedMenu")
  lastOpenendNestedMenuChange(to: Menu) {
    if (this.isNestedMenu) {
      this.$parent.$parent.lastOpenendNestedMenu = to;
    }
  }

  @Watch("showMenu")
  showMenuChange(show: boolean) {
    if (show) {
      // reset scroll
      this.$nextTick(() => {
        (this.$refs.menu as HTMLElement).scrollTop = 0;
      });
    }
    if (this.isNestedMenu) return;
    const activator = this.activator.firstElementChild;
    if (show) {
      activator?.classList.add(this.activatorActiveClass);
      window.addEventListener("scroll", this.handleScroll, true);
      window.addEventListener("keydown", this.handleKeyDown);
    } else {
      activator?.classList.remove(this.activatorActiveClass);
      window.removeEventListener("scroll", this.handleScroll, true);
      window.removeEventListener("keydown", this.handleKeyDown);
    }
  }

  created() {
    this.isNestedMenu = this.$parent.nested;
  }

  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll, true);
    window.removeEventListener("keydown", this.handleKeyDown);
  }

  mounted() {
    if (this.isNestedMenu) {
      this.activator.addEventListener("mouseenter", this.handleMouseEnter);
      this.activator.addEventListener("focusin", () => {
        this.$parent.$parent.selectedNestedMenuItem = this;
      });

      this.activator.addEventListener("focusout", () => {
        if (this.$parent.$parent.selectedNestedMenuItem == this) {
          this.$parent.$parent.selectedNestedMenuItem = null;
        }
      });
      this.$el.addEventListener("mouseleave", this.handleMouseLeave);
    }
    this.itemList = [...this.$el.querySelector(".ogor-menu").children]
      .map((child) => child.querySelector("div > .ogor-menu-container > .ogor-menu-activator > button, div > button"))
      .filter((item) => !!item) as HTMLButtonElement[];
  }

  alignMenu(placementStr: MenuPlacement, anchorStr: MenuAnchors, positionActivator: HTMLDivElement) {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const menuEl = this.$refs.menu as HTMLElement;
    const menuWidth = menuEl.offsetWidth;
    const menuHeight = menuEl.offsetHeight;
    const bounds = (positionActivator || this.activator).getBoundingClientRect();
    const placement = { x: "left", y: "top" };
    const parts = placementStr.split("-");
    placement.x = parts[1];
    placement.y = parts[0];

    const findPlacement = (axis: "x" | "y", preference: "start" | "end", anchorStart: boolean, _callStack = 0) => {
      const axisLength = axis == "x" ? windowWidth : windowHeight;
      const menuLength = axis == "x" ? menuWidth : menuHeight;
      const axisProps = axis == "x" ? ["left", "right"] : ["top", "bottom"];
      let remainingSpace = 0;
      if (preference == "start") {
        if (anchorStart) {
          remainingSpace = bounds[axisProps[0]];
        } else {
          remainingSpace = bounds[axisProps[1]];
        }
      } else if (preference == "end") {
        if (anchorStart) {
          remainingSpace = axisLength - bounds[axisProps[0]];
        } else {
          remainingSpace = axisLength - bounds[axisProps[1]];
        }
      }
      if (menuLength <= remainingSpace) {
        // if placement exceeds viewport
        if (axis == "y") {
          if (bounds[axisProps[1]] > axisLength - 8) {
            return { [axisProps[1]]: 8 }; // push it up
          }
          if (bounds[axisProps[0]] < 8) {
            return { [axisProps[0]]: 8 }; // push it down
          }
        }

        // preference is suitable
        if (preference == "start") {
          if (anchorStart) {
            return { [axisProps[0]]: bounds[axisProps[0]] - menuLength };
          } else {
            return { [axisProps[0]]: bounds[axisProps[1]] - menuLength };
          }
        } else if (preference == "end") {
          if (anchorStart) {
            return { [axisProps[0]]: bounds[axisProps[0]] };
          } else {
            return { [axisProps[0]]: bounds[axisProps[1]] };
          }
        }
      } else {
        // try flip
        if (_callStack == 0) {
          // if inverse hasn't been tried
          let inversePreference: "start" | "end" = preference == "start" ? "end" : "start";
          let result = findPlacement(axis, inversePreference, !anchorStart, 1);
          if (result) {
            return result;
          } else {
            // place at start/end of axis
            if (preference == "start") {
              return { [axisProps[0]]: 16 };
            } else if (preference == "end") {
              return { [axisProps[1]]: 16 };
            }
          }
        }
        // flip has been tried already and it failed
        return null;
      }
    };

    let placementResult = {
      top: null as number,
      left: null as number,
      bottom: null as number,
      right: null as number,
    };

    const anchor = anchorStr.split("-").map((s) => s == "start");
    if (placement.x == "left") {
      placementResult = {
        ...placementResult,
        ...findPlacement("x", "start", anchor[1]),
      };
    } else {
      placementResult = {
        ...placementResult,
        ...findPlacement("x", "end", anchor[1]),
      };
    }

    if (placement.y == "top") {
      placementResult = {
        ...placementResult,
        ...findPlacement("y", "start", anchor[0]),
      };
    } else {
      placementResult = {
        ...placementResult,
        ...findPlacement("y", "end", anchor[0]),
      };
    }

    const resultKeys = Object.keys(placementResult);
    resultKeys.forEach((key) => {
      const value = placementResult[key];
      menuEl.style[key] = value != null ? value + "px" : null;
    });
  }

  openMenu(clickEvent?: MouseEvent) {
    if (this.disabled) return;
    this.showMenu = true;
    this.$emit("open", true);
    let positionActivator: HTMLDivElement = null;
    if (clickEvent) {
      positionActivator = document.createElement("div");
      positionActivator.style.position = "fixed";
      positionActivator.style.visibility = "hidden";
      positionActivator.style.left = clickEvent.clientX + "px";
      positionActivator.style.top = clickEvent.clientY + "px";
      positionActivator.style.padding = this.activatorPadding;
      positionActivator.style.transform = "translate(-50%, -50%)";
      document.body.append(positionActivator);
    }
    this.$nextTick(() => {
      this.alignMenu(this.placement, this.anchors, positionActivator);
      positionActivator?.remove();
    });
  }

  closeMenu() {
    if (!this.beforeClose()) return false;
    if (this.isNestedMenu) {
      this.$parent.closeMenu();
      // we need all menus to fade out simultaneously, so we delay
      // the showMenu set intruction by 300ms (until the root menu ends transitioning)
      window.setTimeout(() => {
        this.showMenu = false;
      }, 300);
    } else {
      this.showMenu = false;
      if (this.lastOpenendNestedMenu) {
        const lastOpened = this.lastOpenendNestedMenu;
        this.lastOpenendNestedMenu = null;
        lastOpened.closeMenu();
      }
    }
    this.$emit("close");
    return true;
  }

  maybeOpenMenu() {
    if (this.isNestedMenu) return;
    this.openMenu();
  }

  handleMouseEnter() {
    this.lastOpenendNestedMenu = this;
    this.selectedItemIndex = null;
    this.openMenu();
  }

  handleMouseLeave() {
    this.showMenu = false;
    const parent = this.lastOpenendNestedMenu.$parent.$parent;
    this.lastOpenendNestedMenu = parent.isNestedMenu ? parent : null;
  }

  focusItem(itemIndex: number) {
    const itemList = this.lastOpenendNestedMenu?.itemList || this.itemList;
    const itemCount = itemList.length;
    if (itemIndex < 0) {
      itemIndex = itemIndex + itemCount;
    }
    itemIndex %= itemCount;
    itemList[itemIndex].focus();
    this.selectedItemIndex = itemIndex;
  }

  handleKeyDown(e: KeyboardEvent) {
    if (["INPUT", "TEXTAREA"].includes(document.activeElement.tagName) && e.key != "Escape") {
      return;
    }
    e.preventDefault();
    if (!this.showMenu) return;
    let itemList: HTMLButtonElement[], button: HTMLButtonElement;
    switch (e.key) {
      case "ArrowDown":
        if (this.selectedItemIndex == null) {
          this.focusItem(0);
        } else {
          this.focusItem(this.selectedItemIndex + 1);
        }
        break;
      case "ArrowUp":
        this.focusItem(this.selectedItemIndex - 1);
        break;
      case " ":
      case "Enter":
        itemList = this.lastOpenendNestedMenu?.itemList || this.itemList;
        button = itemList[this.selectedItemIndex];
        if (!button.dataset.menu) {
          button.click();
          break;
        }
      // falls through
      case "ArrowRight":
        if (this.selectedItemIndex == null) return;
        itemList = this.lastOpenendNestedMenu?.itemList || this.itemList;
        button = itemList[this.selectedItemIndex];
        if (!button.dataset.menu) return;
        this.selectedNestedMenuItem.openMenu();
        this.lastOpenendNestedMenu = this.selectedNestedMenuItem;
        this.selectedNestedMenuItem.lastFocusedIndex = this.selectedItemIndex;
        this.$nextTick(() => {
          this.focusItem(0);
        });
        break;
      case "ArrowLeft":
        if (this.lastOpenendNestedMenu) {
          this.lastOpenendNestedMenu.showMenu = false;
          const focusBack = this.lastOpenendNestedMenu.lastFocusedIndex || 0;
          const parent = this.lastOpenendNestedMenu.$parent.$parent;
          this.lastOpenendNestedMenu = parent.isNestedMenu ? parent : null;
          this.focusItem(focusBack);
        }
        break;
      case "Escape":
        this.closeMenu();
        break;
    }
  }

  handleScroll(e: Event) {
    if (!this.$el.contains(e.target as HTMLElement)) {
      this.closeMenu();
    }
  }
}
